/**
 * AI Trial Room — 2-Camera Shop Owner Flow
 *
 * For shop owners: Click customer photo (Step 1) + apparel photo (Step 2),
 * then AI merges them with automatic background removal + torso alignment.
 *
 * • Uses MediaPipe PoseLandmarker → detect customer torso
 * • Uses MediaPipe ImageSegmenter (selfie_multiclass) → cut out clothes
 * • Gender / Kid preset positioning
 * • Download & WhatsApp share
 *
 * 100% client-side · zero API cost.
 */
import React, { useEffect, useRef, useState, useCallback } from 'react';
import {
  FilesetResolver,
  PoseLandmarker,
  ImageSegmenter,
} from '@mediapipe/tasks-vision';
import { toast } from 'sonner';
import {
  Camera as CameraIcon,
  Upload,
  Download,
  X,
  Loader2,
  RotateCcw,
  Share2,
  ChevronRight,
  ChevronLeft,
  Check,
  RefreshCw,
  Gift,
  Package,
  Eye,
  EyeOff,
} from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import api from '../../utils/api';

const POSE_MODEL =
  'https://storage.googleapis.com/mediapipe-models/pose_landmarker/pose_landmarker_lite/float16/1/pose_landmarker_lite.task';
const SEG_MODEL =
  'https://storage.googleapis.com/mediapipe-models/image_segmenter/selfie_multiclass_256x256/float32/latest/selfie_multiclass_256x256.tflite';
const WASM_URL =
  'https://cdn.jsdelivr.net/npm/@mediapipe/tasks-vision@0.10.35/wasm';

// MediaPipe multiclass selfie segmenter category ids
// 0=background, 1=hair, 2=body-skin, 3=face-skin, 4=clothes, 5=others (accessories)
const CLOTHES_CAT = 4;

// Pose landmark indexes (33-point)
const P = { lS: 11, rS: 12, lH: 23, rH: 24, nose: 0 };

// Character presets (adjusts torso proportions for scaling)
const CHARACTERS = [
  { id: 'male',   label: 'Male',   emoji: '🧑',        widthMul: 2.2, heightMul: 2.7, topOff: 0.30 },
  { id: 'female', label: 'Female', emoji: '👩',        widthMul: 2.3, heightMul: 3.0, topOff: 0.28 },
  { id: 'boy',    label: 'Boy',    emoji: '👦',        widthMul: 2.0, heightMul: 2.4, topOff: 0.28 },
  { id: 'girl',   label: 'Girl',   emoji: '👧',        widthMul: 2.1, heightMul: 2.6, topOff: 0.28 },
];

// ────────────────────────────────────────────────────────────────────────
// Camera helper (used by both steps)
// ────────────────────────────────────────────────────────────────────────
const useCamera = () => {
  const videoRef = useRef(null);
  const streamRef = useRef(null);
  const [active, setActive] = useState(false);

  const start = useCallback(async (facing = 'environment') => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: facing, width: { ideal: 1280 }, height: { ideal: 960 } },
        audio: false,
      });
      streamRef.current = stream;
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        await videoRef.current.play();
      }
      setActive(true);
    } catch (e) {
      toast.error(`Camera access denied — ${e.message}`);
    }
  }, []);

  const stop = useCallback(() => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
    }
    setActive(false);
  }, []);

  const capture = useCallback(() => {
    if (!videoRef.current || !active) return null;
    const v = videoRef.current;
    const c = document.createElement('canvas');
    c.width = v.videoWidth;
    c.height = v.videoHeight;
    c.getContext('2d').drawImage(v, 0, 0, c.width, c.height);
    return c.toDataURL('image/jpeg', 0.92);
  }, [active]);

  useEffect(() => () => stop(), [stop]);

  return { videoRef, active, start, stop, capture };
};

// Load DataURL / URL → HTMLImageElement
const loadImg = (src) =>
  new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => resolve(img);
    img.onerror = reject;
    img.src = src;
  });

// ────────────────────────────────────────────────────────────────────────
// Segment clothes from apparel image using MediaPipe multiclass segmenter.
// Returns a canvas with alpha-cutout of clothes only.
// ────────────────────────────────────────────────────────────────────────
const segmentClothes = async (segmenter, img) => {
  const off = document.createElement('canvas');
  off.width = img.naturalWidth;
  off.height = img.naturalHeight;
  const octx = off.getContext('2d');
  octx.drawImage(img, 0, 0);

  // Run segmenter → returns category mask (Uint8Array) same size as input
  const result = segmenter.segment(off);
  const mask = result?.categoryMask;
  if (!mask) throw new Error('Segmentation failed');

  const mw = mask.width, mh = mask.height;
  const maskData = mask.getAsUint8Array();

  // Build alpha mask matching apparel image size
  const cutout = document.createElement('canvas');
  cutout.width = off.width;
  cutout.height = off.height;
  const cctx = cutout.getContext('2d');
  const src = octx.getImageData(0, 0, off.width, off.height);
  const dst = cctx.createImageData(off.width, off.height);

  // Scale mask coordinates → source pixel coordinates
  const sx = mw / off.width;
  const sy = mh / off.height;

  let anyClothes = false;
  for (let y = 0; y < off.height; y++) {
    for (let x = 0; x < off.width; x++) {
      const mi = Math.floor(y * sy) * mw + Math.floor(x * sx);
      const di = (y * off.width + x) * 4;
      const category = maskData[mi];
      const keep = category === CLOTHES_CAT;
      dst.data[di]     = src.data[di];
      dst.data[di + 1] = src.data[di + 1];
      dst.data[di + 2] = src.data[di + 2];
      dst.data[di + 3] = keep ? src.data[di + 3] : 0;
      if (keep) anyClothes = true;
    }
  }
  cctx.putImageData(dst, 0, 0);
  try { mask.close?.(); } catch (_) { /* noop */ }

  if (!anyClothes) {
    // fallback: return the full image (no clothes detected — probably flat product photo)
    return { canvas: off, hadClothes: false };
  }

  // Crop to non-transparent bbox to remove empty margins
  const bbox = getAlphaBBox(dst.data, off.width, off.height);
  if (!bbox) return { canvas: cutout, hadClothes: true };
  const cropped = document.createElement('canvas');
  cropped.width  = bbox.w;
  cropped.height = bbox.h;
  cropped.getContext('2d').drawImage(cutout, -bbox.x, -bbox.y);
  return { canvas: cropped, hadClothes: true };
};

const getAlphaBBox = (data, w, h) => {
  let minX = w, minY = h, maxX = -1, maxY = -1;
  for (let y = 0; y < h; y++) {
    for (let x = 0; x < w; x++) {
      if (data[(y * w + x) * 4 + 3] > 20) {
        if (x < minX) minX = x; if (y < minY) minY = y;
        if (x > maxX) maxX = x; if (y > maxY) maxY = y;
      }
    }
  }
  if (maxX < 0) return null;
  return { x: minX, y: minY, w: maxX - minX + 1, h: maxY - minY + 1 };
};

// ────────────────────────────────────────────────────────────────────────
// Step badge (stateless — kept outside parent to avoid remount)
// ────────────────────────────────────────────────────────────────────────
const StepBadge = ({ n, label, step }) => (
  <div className={`flex items-center gap-1.5 px-2 py-1 rounded-full text-[10px] font-black ${step === n ? 'bg-fuchsia-500 text-white' : step > n ? 'bg-emerald-500/20 text-emerald-300 border border-emerald-400/40' : 'bg-white/5 text-slate-400 border border-white/10'}`}>
    {step > n ? <Check className="w-3 h-3" /> : <span className="w-4 h-4 rounded-full bg-black/40 flex items-center justify-center text-[9px]">{n}</span>}
    {label}
  </div>
);

// ────────────────────────────────────────────────────────────────────────
// Quota Badge — shows user's daily/monthly remaining
// ────────────────────────────────────────────────────────────────────────
const QuotaBadge = () => {
  const [q, setQ] = React.useState(null);
  React.useEffect(() => {
    api.get('/trial-room/quota').then((r) => setQ(r.data)).catch(() => {});
  }, []);
  if (!q) return null;

  // Free tier response has no daily/monthly limit fields — show free trials remaining instead.
  if (q.tier === 'free') {
    const remaining = q.free_trials_remaining ?? 0;
    const near = remaining <= 1;
    return (
      <div className={`rounded-lg border px-2 py-1 text-[9px] flex items-center justify-between gap-2 ${near ? 'border-red-400/60 bg-red-500/10 text-red-200' : 'border-emerald-400/40 bg-emerald-500/10 text-emerald-200'}`} data-testid="shopflow-quota">
        <div>
          <span className="font-black">🎁 Free Trials:</span> {remaining}/{q.free_lifetime_trials ?? '—'} left
        </div>
      </div>
    );
  }

  const near = (q.daily_remaining ?? Infinity) <= 3;
  return (
    <div className={`rounded-lg border px-2 py-1 text-[9px] flex items-center justify-between gap-2 ${near ? 'border-red-400/60 bg-red-500/10 text-red-200' : 'border-emerald-400/40 bg-emerald-500/10 text-emerald-200'}`} data-testid="shopflow-quota">
      <div>
        <span className="font-black">📊 Today:</span> {q.daily_used ?? '—'}/{q.daily_limit ?? '—'} · <span className="font-black">Month:</span> {q.monthly_used ?? '—'}/{q.monthly_limit ?? '—'}
      </div>
      <div className="font-mono">₹{(q.est_cost_today_inr ?? 0).toFixed(2)}</div>
    </div>
  );
};

// ────────────────────────────────────────────────────────────────────────
// Main component
// ────────────────────────────────────────────────────────────────────────
const TrialRoomShopFlow = ({ onClose }) => {
  const { user } = useAuth();
  const [step, setStep] = useState(1); // 1=customer, 2=apparel, 3=merge
  const [customerImg, setCustomerImg] = useState(null); // dataURL
  const [apparelImg, setApparelImg]   = useState(null); // dataURL
  const [character, setCharacter]     = useState('female');
  const [scale, setScale]     = useState(1);
  const [offsetY, setOffsetY] = useState(0);
  const [offsetX, setOffsetX] = useState(0);
  const [processing, setProcessing] = useState(false);
  const [modelStatus, setModelStatus] = useState('Loading AI models…');
  const [modelReady, setModelReady]   = useState(false);
  const [status, setStatus] = useState('');
  const [includeReferral, setIncludeReferral] = useState(true);
  // 🆕 Compare mode + catalog + watermark
  const [compareMode, setCompareMode] = useState(false); // false = merged, true = original
  const [showCatalog, setShowCatalog] = useState(false);
  const [catalogProducts, setCatalogProducts] = useState([]);
  const [selectedCatalog, setSelectedCatalog] = useState(null); // {id, name, image_url}
  const [watermark, setWatermark] = useState({ enabled: true, shop_name: 'Anant Trial Room', shop_logo_url: '' });
  const [lighting, setLighting] = useState('natural');
  const [angle, setAngle] = useState('front');

  // Referral booster: build viral WhatsApp message
  const referralCode = user?.referral_code || user?.username || '';
  const referralLink = referralCode
    ? `${window.location.origin}/trial-room?ref=${encodeURIComponent(referralCode)}`
    : `${window.location.origin}/trial-room`;
  const buildShareText = () => {
    const lines = [
      '🪞✨ *AI Trial Room · Anant Web Portal*',
      'Yeh mera Virtual Try-On preview dekho! 👗',
      '',
      '🎁 Aap bhi try karein — abhi sign-up karo aur paayen *₹50 AT Wallet Bonus* (referral offer)!',
    ];
    if (includeReferral) lines.push(`🔗 ${referralLink}`);
    lines.push('', '#AnantTrialRoom #VirtualTryOn #AIFashion');
    return lines.join('\n');
  };

  const poseRef = useRef(null);
  const segRef  = useRef(null);
  const canvasRef = useRef(null);
  const watermarkLogoRef = useRef(null); // preloaded logo Image

  // Cached refs for merged rendering
  const custImgElRef = useRef(null);
  const custPoseRef  = useRef(null);
  const apparelCutoutRef = useRef(null);

  const cam = useCamera();
  const fileRef = useRef(null);

  // Fetch user's OWN branding (falls back to platform default)
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await api.get('/trial-room/my-branding').catch(() => null);
        if (cancelled || !r) return;
        const wm = r.data || {};
        setWatermark({
          enabled: wm.enabled !== false,
          shop_name: wm.shop_name || 'Anant Trial Room',
          shop_logo_url: wm.shop_logo_url || '',
        });
        if (wm.shop_logo_url) {
          const logo = new Image();
          logo.crossOrigin = 'anonymous';
          logo.onload  = () => { watermarkLogoRef.current = logo; };
          logo.onerror = () => { watermarkLogoRef.current = null; };
          logo.src = wm.shop_logo_url;
        }
      } catch (_) { /* silent */ }
    })();
    return () => { cancelled = true; };
  }, []);

  // Load models once
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const resolver = await FilesetResolver.forVisionTasks(WASM_URL);
        setModelStatus('Loading Pose model…');
        const pl = await PoseLandmarker.createFromOptions(resolver, {
          baseOptions: { modelAssetPath: POSE_MODEL, delegate: 'GPU' },
          runningMode: 'IMAGE', numPoses: 1,
        });
        if (cancelled) { pl.close(); return; }
        poseRef.current = pl;
        setModelStatus('Loading Segmenter…');
        const seg = await ImageSegmenter.createFromOptions(resolver, {
          baseOptions: { modelAssetPath: SEG_MODEL, delegate: 'GPU' },
          runningMode: 'IMAGE',
          outputCategoryMask: true, outputConfidenceMasks: false,
        });
        if (cancelled) { seg.close(); return; }
        segRef.current = seg;
        setModelReady(true);
        setModelStatus('');
      } catch (e) {
        if (!cancelled) { setModelStatus(''); toast.error(`Model load failed: ${e.message || e}`); }
      }
    })();
    return () => {
      cancelled = true;
      try { poseRef.current?.close(); } catch (_) { /* noop */ }
      try { segRef.current?.close();  } catch (_) { /* noop */ }
    };
  }, []);

  // ── Capture / Upload helpers ────────────────────────────────────────────
  const captureFromCamera = () => {
    const dataUrl = cam.capture();
    if (!dataUrl) { toast.error('Camera not ready'); return; }
    handleImageSet(dataUrl);
  };

  const handleFile = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > 8 * 1024 * 1024) { toast.error('Photo <8 MB honi chahiye'); return; }
    const r = new FileReader();
    r.onload = (ev) => handleImageSet(ev.target.result);
    r.readAsDataURL(f);
    e.target.value = '';
  };

  const handleImageSet = (dataUrl) => {
    cam.stop();
    if (step === 1) {
      setCustomerImg(dataUrl);
      toast.success('✅ Customer photo captured!');
    } else if (step === 2) {
      setApparelImg(dataUrl);
      toast.success('✅ Apparel photo captured!');
    }
  };

  // ── AI VTON (Nano Banana / Gemini image edit) — TRUE virtual try-on ──────
  // This calls backend which uses Gemini image-edit model. Person actually WEARS the apparel.
  const [aiResultImg, setAiResultImg] = useState(null); // dataURL of Gemini output
  // 🆕 Stable saved snapshot of the final merged image (Blob URL). Persists even if canvas re-renders.
  // Used by Download + WhatsApp share to guarantee the try-on is what gets exported (not blank / not original).
  const [finalBlobUrl, setFinalBlobUrl] = useState(null);
  const finalBlobRef = useRef(null); // raw Blob for share API
  // 🆕 Feedback state — user rates result 👍/👎; 👎 triggers a retry with hardened prompt (once)
  const [lastCacheKey, setLastCacheKey] = useState(null);
  const [feedbackRating, setFeedbackRating] = useState(null); // 'up' | 'down' | null
  const [retryAttempted, setRetryAttempted] = useState(false);
  const snapshotFinal = useCallback(() => {
    const c = canvasRef.current; if (!c) return;
    try {
      c.toBlob((blob) => {
        if (!blob) return; // canvas tainted → toBlob returns null → keep previous snapshot
        const url = URL.createObjectURL(blob);
        setFinalBlobUrl((prev) => {
          if (prev && prev.startsWith('blob:')) { try { URL.revokeObjectURL(prev); } catch { /* noop */ } }
          return url;
        });
        finalBlobRef.current = blob;
      }, 'image/jpeg', 0.92);
    } catch { /* silent — tainted canvas */ }
  }, []);
  const [usedAiVton, setUsedAiVton] = useState(false);

  const doMerge = useCallback(async (retryBoost = false) => {
    if (!customerImg || !apparelImg) return;
    setProcessing(true);
    // Reset feedback state on new attempt
    if (!retryBoost) { setFeedbackRating(null); setRetryAttempted(false); }
    setStatus(retryBoost
      ? '🔄 Retry with stronger prompt… (15-30 seconds)'
      : '🤖 Real AI Try-On chal raha hai (Gemini Nano Banana)… 15-30 seconds…');
    try {
      // Try AI VTON first (real "person wearing apparel")
      const r = await api.post('/trial-room/vton-merge', {
        customer_image: customerImg,
        apparel_image: apparelImg,
        character,
        apparel_type: selectedCatalog?.name?.toLowerCase()?.slice(0, 40) || 'top',
        lighting, angle, save_history: true,
        retry_boost: !!retryBoost,
      }, { timeout: 90000 });
      if (r.data?.image_base64) {
        const mime = r.data.mime_type || 'image/jpeg';
        const dataUrl = `data:${mime};base64,${r.data.image_base64}`;
        setAiResultImg(dataUrl);
        setUsedAiVton(true);
        setLastCacheKey(r.data.cache_key || null);
        // Preload into custImgElRef so canvas renders it
        const img = await loadImg(dataUrl);
        custImgElRef.current = img;
        // Draw directly on canvas (no pose overlay — the AI already merged it)
        renderAiResult(img);
        setStatus('');
        setStep(3);
        // Track usage
        try {
          await api.post('/trial-room/track-usage', {
            flow: 'shop_owner_ai_vton',
            category: 'apparels',
            product_id: selectedCatalog?.id || null,
            product_name: selectedCatalog?.name || null,
            character,
          });
        } catch (_) { /* silent */ }
        toast.success(retryBoost ? '✅ Retry ready! Better result?' : '✅ AI Try-On ready! Real wearing preview.');
        return;
      }
      throw new Error('AI returned no image');
    } catch (e) {
      const msg = e.response?.data?.detail || e.message || 'AI VTON failed';
      toast.error(`⚠️ AI VTON failed (${msg}). Fallback overlay try kar raha hoon…`, { duration: 4000 });
      // Fallback to local overlay
      try {
        setStatus('🔍 Local fallback — detecting body & overlaying…');
        await runLocalOverlay();
        setUsedAiVton(false);
        setStep(3);
        setStatus('');
      } catch (fallbackErr) {
        toast.error(`❌ Fallback also failed: ${fallbackErr.message || fallbackErr}`);
        setStatus('');
      }
    } finally { setProcessing(false); }
  }, [customerImg, apparelImg, character, selectedCatalog, lighting, angle]);

  // 👍/👎 feedback — logs signal + auto-retries on 👎 (once) with stronger prompt
  const submitFeedback = useCallback(async (rating) => {
    if (feedbackRating) return; // one shot
    setFeedbackRating(rating);
    try {
      await api.post('/trial-room/vton-feedback', {
        rating,
        cache_key: lastCacheKey,
        kind: 'apparel',
        apparel_type: selectedCatalog?.name?.toLowerCase()?.slice(0, 40) || 'top',
        character, lighting, angle,
        was_retry: retryAttempted,
      });
    } catch (_) { /* silent */ }
    if (rating === 'up') {
      toast.success('🙏 Shukriya! Feedback saved.');
    } else {
      toast('👎 Noted. Ek stronger retry chala rahe hain…', { duration: 3000 });
      if (!retryAttempted) {
        setRetryAttempted(true);
        setFeedbackRating(null); // allow re-rating the retry
        // Small delay for toast
        setTimeout(() => doMerge(true), 400);
      }
    }
  }, [feedbackRating, lastCacheKey, selectedCatalog, character, lighting, angle, retryAttempted, doMerge]);

  // Renders the AI-generated try-on image directly (no torso positioning needed)
  const renderAiResult = useCallback((img) => {
    const canvas = canvasRef.current;
    if (!canvas || !img) return;
    const MAX = 900;
    let w = img.naturalWidth, h = img.naturalHeight;
    const r = w / h;
    if (w > MAX) { w = MAX; h = MAX / r; }
    if (h > MAX) { h = MAX; w = MAX * r; }
    canvas.width = w; canvas.height = h;
    const ctx = canvas.getContext('2d');
    ctx.clearRect(0, 0, w, h);
    ctx.drawImage(img, 0, 0, w, h);
    drawWatermark(ctx, w, h);
    // 🆕 Snapshot stable final image (fixes: blank after few seconds + WhatsApp share blank)
    setTimeout(() => snapshotFinal(), 100);
  }, [watermark, snapshotFinal]);

  // Local (MediaPipe) fallback merge — only if AI VTON fails
  const runLocalOverlay = async () => {
    if (!modelReady) throw new Error('AI models not loaded');
    const custEl = await loadImg(customerImg);
    const poseResult = poseRef.current.detect(custEl);
    let pose = poseResult?.landmarks?.[0];
    if (!pose) {
      pose = new Array(33).fill(null).map(() => ({ x: 0.5, y: 0.5, z: 0 }));
      pose[11] = { x: 0.35, y: 0.35, z: 0 };
      pose[12] = { x: 0.65, y: 0.35, z: 0 };
      pose[23] = { x: 0.38, y: 0.70, z: 0 };
      pose[24] = { x: 0.62, y: 0.70, z: 0 };
      toast('⚠️ Body auto-detect nahi hua — sliders se adjust karo.', { duration: 4000 });
    }
    custImgElRef.current = custEl;
    custPoseRef.current  = pose;
    const appEl = await loadImg(apparelImg);
    const { canvas: cutout } = await segmentClothes(segRef.current, appEl);
    apparelCutoutRef.current = cutout;
    renderMerged();
  };

  // ── Catalog picker (fetch pre-uploaded apparels) ────────────────────────
  const openCatalog = async () => {
    try {
      if (catalogProducts.length === 0) {
        // Merge public + user's private (admin-assigned) apparels
        const [pub, mine] = await Promise.all([
          api.get('/trial-room/products?category=apparels'),
          api.get('/trial-room/my-assigned-products?category=apparels').catch(() => ({ data: { products: [] } })),
        ]);
        const merged = [...(mine.data?.products || []), ...(pub.data?.products || [])];
        setCatalogProducts(merged);
      }
      setShowCatalog(true);
    } catch (e) {
      toast.error('Catalog load failed');
    }
  };

  const pickFromCatalog = async (product) => {
    // Convert image URL to dataURL for consistent processing
    try {
      const r = await fetch(product.image_url);
      const blob = await r.blob();
      const reader = new FileReader();
      reader.onload = (ev) => {
        setApparelImg(ev.target.result);
        setSelectedCatalog({ id: product.id, name: product.name });
        setShowCatalog(false);
        toast.success(`✅ ${product.name} selected from catalog`);
      };
      reader.readAsDataURL(blob);
    } catch (e) {
      // Fallback: use URL directly (may fail on CORS)
      setApparelImg(product.image_url);
      setSelectedCatalog({ id: product.id, name: product.name });
      setShowCatalog(false);
      toast.success(`✅ ${product.name} selected`);
    }
  };

  // ── Draw watermark (text + optional logo) at bottom-right ────────────────
  const drawWatermark = (ctx, w, h) => {
    if (!watermark.enabled) return;
    const label = watermark.shop_name || 'Anant Trial Room';
    const pad = Math.max(10, Math.round(w * 0.015));
    // Font size responsive to canvas width
    const fontSize = Math.max(14, Math.round(w * 0.028));
    ctx.save();
    ctx.font = `900 ${fontSize}px system-ui, -apple-system, sans-serif`;
    const textWidth = ctx.measureText(label).width;
    const logo = watermarkLogoRef.current;
    const logoSize = fontSize * 1.4;
    const badgeW = textWidth + (logo ? logoSize + pad * 0.6 : 0) + pad * 1.4;
    const badgeH = Math.max(fontSize + pad * 0.9, logoSize + pad * 0.6);
    const bx = w - badgeW - pad;
    const by = h - badgeH - pad;
    // Rounded translucent pill
    ctx.fillStyle = 'rgba(0,0,0,0.55)';
    const r = badgeH / 2;
    ctx.beginPath();
    ctx.moveTo(bx + r, by);
    ctx.arcTo(bx + badgeW, by, bx + badgeW, by + badgeH, r);
    ctx.arcTo(bx + badgeW, by + badgeH, bx, by + badgeH, r);
    ctx.arcTo(bx, by + badgeH, bx, by, r);
    ctx.arcTo(bx, by, bx + badgeW, by, r);
    ctx.closePath();
    ctx.fill();
    // Logo (left of text)
    let textX = bx + pad * 0.7;
    if (logo) {
      const lx = bx + pad * 0.5;
      const ly = by + (badgeH - logoSize) / 2;
      ctx.drawImage(logo, lx, ly, logoSize, logoSize);
      textX = lx + logoSize + pad * 0.4;
    }
    // Text
    ctx.fillStyle = '#ffffff';
    ctx.textBaseline = 'middle';
    ctx.fillText(label, textX, by + badgeH / 2 + 1);
    // subtle top highlight
    ctx.strokeStyle = 'rgba(255,255,255,0.15)';
    ctx.lineWidth = 1;
    ctx.stroke();
    ctx.restore();
  };

  // ── Draw customer + overlay apparel on canvas ────────────────────────────
  const renderMerged = useCallback(() => {
    const canvas = canvasRef.current;
    const custEl = custImgElRef.current;
    if (!canvas || !custEl) return;

    // 🤖 AI VTON path — customer image IS the final composed try-on. Just draw + watermark.
    if (usedAiVton && !compareMode) {
      renderAiResult(custEl);
      return;
    }

    const pose   = custPoseRef.current;
    const cutout = apparelCutoutRef.current;

    const MAX = 900;
    let w = custEl.naturalWidth, h = custEl.naturalHeight;
    const ratio = w / h;
    if (w > MAX) { w = MAX; h = MAX / ratio; }
    if (h > MAX) { h = MAX; w = MAX * ratio; }
    canvas.width = w; canvas.height = h;
    const ctx = canvas.getContext('2d');
    ctx.clearRect(0, 0, w, h);
    ctx.drawImage(custEl, 0, 0, w, h);

    // Compare mode: skip apparel overlay to show ORIGINAL customer photo
    if (!compareMode && !usedAiVton && pose && cutout) {
      const lS = pose[P.lS], rS = pose[P.rS], lH = pose[P.lH], rH = pose[P.rH];
      const shoulderMid = { x: ((lS.x + rS.x) / 2) * w, y: ((lS.y + rS.y) / 2) * h };
      const hipMid      = { x: ((lH.x + rH.x) / 2) * w, y: ((lH.y + rH.y) / 2) * h };
      const shoulderWidth = Math.abs((rS.x - lS.x)) * w;
      const torsoHeight   = Math.abs(hipMid.y - shoulderMid.y);

      const preset = CHARACTERS.find((c) => c.id === character) || CHARACTERS[1];

      // Preserve cutout aspect ratio while fitting to torso
      const targetTorsoW = shoulderWidth * preset.widthMul * scale;
      const cutoutRatio  = cutout.width / cutout.height;
      let drawW = targetTorsoW;
      let drawH = drawW / cutoutRatio;
      const maxH = torsoHeight * preset.heightMul * scale;
      if (drawH > maxH) { drawH = maxH; drawW = drawH * cutoutRatio; }

      const x = shoulderMid.x - drawW / 2 + offsetX;
      const y = shoulderMid.y - shoulderWidth * preset.topOff + offsetY;
      ctx.drawImage(cutout, x, y, drawW, drawH);
    }
    // Watermark (always burned in)
    drawWatermark(ctx, w, h);
    // "ORIGINAL" tag in compare mode
    if (compareMode) {
      const fs = Math.max(14, Math.round(w * 0.028));
      ctx.save();
      ctx.font = `900 ${fs}px system-ui, sans-serif`;
      const label = '📷 ORIGINAL';
      const tw = ctx.measureText(label).width;
      const pad = 10;
      ctx.fillStyle = 'rgba(0,0,0,0.65)';
      ctx.fillRect(pad, pad, tw + pad * 2, fs + pad);
      ctx.fillStyle = '#fbbf24';
      ctx.textBaseline = 'top';
      ctx.fillText(label, pad * 2, pad + 4);
      ctx.restore();
    }
    // "AI TRY-ON" badge when using Nano Banana
    if (!compareMode && usedAiVton) {
      const fs = Math.max(14, Math.round(w * 0.028));
      ctx.save();
      ctx.font = `900 ${fs}px system-ui, sans-serif`;
      const label = '🤖 AI TRY-ON';
      const tw = ctx.measureText(label).width;
      const pad = 10;
      ctx.fillStyle = 'rgba(217, 70, 239, 0.85)';
      ctx.fillRect(pad, pad, tw + pad * 2, fs + pad);
      ctx.fillStyle = '#ffffff';
      ctx.textBaseline = 'top';
      ctx.fillText(label, pad * 2, pad + 4);
      ctx.restore();
    }
  }, [character, scale, offsetY, offsetX, compareMode, watermark, usedAiVton, renderAiResult]);

  useEffect(() => { if (step === 3) { renderMerged(); setTimeout(() => snapshotFinal(), 150); } }, [step, renderMerged, snapshotFinal]);

  // ── Download / Share (use stable snapshot, NOT live canvas) ─────────────
  const download = () => {
    const blob = finalBlobRef.current;
    if (!blob) { toast.error('❌ Final image not ready yet. Wait a moment and retry.'); return; }
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = `TrialRoom-Shop-${Date.now()}.jpg`; a.click();
    setTimeout(() => URL.revokeObjectURL(url), 5000);
    toast.success('✅ Saved to device');
  };

  const shareWhatsApp = async () => {
    const blob = finalBlobRef.current;
    if (!blob) { toast.error('❌ Final image not ready yet — wait a moment.'); return; }
    if (blob.size < 5000) { toast.error('❌ Image export blank (possible watermark CORS issue). Please retry.'); return; }
    const shareText = buildShareText();
    try {
      const file = new File([blob], `trial-room-${Date.now()}.jpg`, { type: 'image/jpeg' });
      if (navigator.canShare && navigator.canShare({ files: [file] })) {
        await navigator.share({ files: [file], title: 'AI Trial Room · Anant Web Portal', text: shareText });
      } else {
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url; a.download = `TrialRoom-Shop-${Date.now()}.jpg`; a.click();
        setTimeout(() => URL.revokeObjectURL(url), 5000);
        window.open(`https://wa.me/?text=${encodeURIComponent(shareText)}`, '_blank');
        toast('📱 Image downloaded — attach it in WhatsApp (message pre-filled)', { duration: 5000 });
      }
    } catch (e) { toast.error(`Share failed: ${e.message}`); }
  };

  // ── Step reset helpers ──────────────────────────────────────────────────
  const retakeCustomer = () => { setCustomerImg(null); setStep(1); cam.stop(); setAiResultImg(null); setUsedAiVton(false); };
  const retakeApparel  = () => { setApparelImg(null); setSelectedCatalog(null); setStep(2); cam.stop(); setAiResultImg(null); setUsedAiVton(false); };
  const resetAll = () => {
    setCustomerImg(null); setApparelImg(null); setSelectedCatalog(null);
    setScale(1); setOffsetX(0); setOffsetY(0); setCharacter('female'); setCompareMode(false);
    setAiResultImg(null); setUsedAiVton(false);
    setStep(1); cam.stop();
  };

  // ────────────────────────────────────────────────────────────────────────
  // Renders
  // ────────────────────────────────────────────────────────────────────────

  return (
    <div className="fixed inset-0 z-50 bg-black flex flex-col" data-testid="trial-room-shop-flow">
      {/* HEADER */}
      <div className="p-3 bg-slate-950 border-b border-white/10 flex items-center justify-between flex-wrap gap-2">
        <div className="min-w-0">
          <div className="text-[10px] uppercase tracking-widest text-fuchsia-300 font-black">🛍️ Shop Owner · 2-Camera Try-On</div>
          <h3 className="text-sm md:text-base font-black text-white truncate">Customer + Apparel = Instant Preview</h3>
        </div>
        <div className="flex items-center gap-1.5 flex-wrap">
          <StepBadge n={1} label="Customer" step={step} />
          <ChevronRight className="w-3 h-3 text-slate-500" />
          <StepBadge n={2} label="Apparel" step={step} />
          <ChevronRight className="w-3 h-3 text-slate-500" />
          <StepBadge n={3} label="Preview" step={step} />
          <button onClick={onClose} data-testid="shopflow-close" className="ml-2 p-2 rounded-lg bg-white/10 hover:bg-white/20"><X className="w-4 h-4" /></button>
        </div>
      </div>

      {/* MODEL LOADING */}
      {!modelReady && (
        <div className="flex-1 flex flex-col items-center justify-center text-white">
          <Loader2 className="w-10 h-10 animate-spin text-fuchsia-400" />
          <div className="mt-3 text-sm">{modelStatus}</div>
          <div className="text-[10px] text-slate-500 mt-1">One-time · runs 100% in your browser</div>
        </div>
      )}

      {/* STEP 1 / 2 — capture UI */}
      {modelReady && (step === 1 || step === 2) && (
        <div className="flex-1 flex flex-col items-center justify-center bg-slate-950 overflow-auto p-4">
          <div className="w-full max-w-lg">
            <div className="text-center mb-3">
              <div className="text-lg md:text-xl font-black text-white">
                {step === 1 ? '📸 Step 1: Customer ka Photo' : '👕 Step 2: Dress / Apparel ka Photo'}
              </div>
              <div className="text-xs text-slate-400 mt-1">
                {step === 1 ? 'Customer ko sedha khada karke torso/full-body click karein' : 'Dress ka clear photo — plain background sabse better'}
              </div>
              {step === 1 && !customerImg && (
                <div className="mt-2 rounded-lg border border-fuchsia-400/30 bg-fuchsia-500/5 px-2 py-1.5 text-[10px] text-fuchsia-100">
                  <div className="font-black text-fuchsia-300 uppercase tracking-widest mb-0.5">💡 Best Photo Tips:</div>
                  <div className="flex gap-1.5 flex-wrap justify-center">
                    <span className="px-2 py-0.5 rounded-full bg-emerald-500/20 border border-emerald-400/40 text-emerald-200">✅ Full Body</span>
                    <span className="px-2 py-0.5 rounded-full bg-emerald-500/20 border border-emerald-400/40 text-emerald-200">✅ Half Body (torso visible)</span>
                    <span className="px-2 py-0.5 rounded-full bg-red-500/20 border border-red-400/40 text-red-200">❌ Face-only</span>
                  </div>
                  <div className="mt-1 text-slate-300">Plain light background · Straight pose · Both shoulders visible</div>
                </div>
              )}
            </div>

            {/* Camera preview or preview of captured image */}
            <div className="rounded-2xl border-2 border-fuchsia-400/40 bg-black/60 overflow-hidden aspect-[3/4] flex items-center justify-center relative">
              {step === 1 && customerImg && (
                <img src={customerImg} alt="Customer" className="w-full h-full object-contain" />
              )}
              {step === 2 && apparelImg && (
                <img src={apparelImg} alt="Apparel" className="w-full h-full object-contain" />
              )}
              {!(
                (step === 1 && customerImg) ||
                (step === 2 && apparelImg)
              ) && (
                <>
                  <video ref={cam.videoRef} playsInline muted className={`w-full h-full object-cover ${cam.active ? '' : 'opacity-0'}`} />
                  {/* 👤 Body silhouette guide (Step 1 only) */}
                  {step === 1 && cam.active && (
                    <svg
                      viewBox="0 0 100 140"
                      className="absolute inset-0 w-full h-full pointer-events-none"
                      preserveAspectRatio="xMidYMid meet"
                      data-testid="shopflow-silhouette"
                    >
                      <g fill="none" stroke="rgba(232,121,249,0.6)" strokeWidth="0.6" strokeDasharray="1.5 1.5">
                        {/* Head */}
                        <circle cx="50" cy="22" r="10" />
                        {/* Neck */}
                        <line x1="50" y1="32" x2="50" y2="38" />
                        {/* Shoulders */}
                        <line x1="34" y1="42" x2="66" y2="42" />
                        {/* Torso outline (variant by character) */}
                        {character === 'female' || character === 'girl' ? (
                          <path d="M 34 42 L 32 66 L 38 88 L 42 108 L 42 120 M 66 42 L 68 66 L 62 88 L 58 108 L 58 120" />
                        ) : (
                          <path d="M 34 42 L 32 68 L 36 92 L 40 112 L 40 122 M 66 42 L 68 68 L 64 92 L 60 112 L 60 122" />
                        )}
                        {/* Arms */}
                        <line x1="34" y1="42" x2="26" y2="72" />
                        <line x1="66" y1="42" x2="74" y2="72" />
                      </g>
                      <text x="50" y="135" textAnchor="middle" fill="rgba(232,121,249,0.85)" fontSize="4" fontWeight="900">
                        Stand inside outline · {character.toUpperCase()}
                      </text>
                    </svg>
                  )}
                  {!cam.active && (
                    <div className="absolute inset-0 flex flex-col items-center justify-center text-slate-400">
                      <CameraIcon className="w-14 h-14 mb-2 opacity-60" />
                      <div className="text-xs">Camera off — Start karein ya photo upload karein</div>
                    </div>
                  )}
                </>
              )}
            </div>

            {/* Action buttons */}
            <div className="mt-3 grid grid-cols-2 gap-2">
              {!cam.active && !(step === 1 ? customerImg : apparelImg) && (
                <>
                  <button
                    onClick={() => cam.start(step === 1 ? 'user' : 'environment')}
                    data-testid={`shopflow-start-cam-${step}`}
                    className="col-span-2 sm:col-span-1 px-3 py-2.5 rounded-lg bg-gradient-to-r from-fuchsia-500 to-rose-500 text-white text-xs font-black flex items-center justify-center gap-1.5 hover:brightness-110"
                  >
                    <CameraIcon className="w-4 h-4" /> Camera Start
                  </button>
                  <button
                    onClick={() => fileRef.current?.click()}
                    data-testid={`shopflow-upload-${step}`}
                    className="col-span-2 sm:col-span-1 px-3 py-2.5 rounded-lg bg-white/10 hover:bg-white/20 text-white text-xs font-black flex items-center justify-center gap-1.5"
                  >
                    <Upload className="w-4 h-4" /> Upload Photo
                  </button>
                  <input ref={fileRef} type="file" accept="image/*" capture={step === 1 ? 'user' : 'environment'} onChange={handleFile} className="hidden" />
                  {step === 2 && (
                    <button
                      onClick={openCatalog}
                      data-testid="shopflow-choose-catalog"
                      className="col-span-2 px-3 py-2 rounded-lg border-2 border-amber-400/60 bg-amber-500/10 hover:bg-amber-500/20 text-amber-200 text-xs font-black flex items-center justify-center gap-1.5"
                    >
                      <Package className="w-4 h-4" /> Choose from Catalog
                    </button>
                  )}
                </>
              )}

              {cam.active && (
                <>
                  <button
                    onClick={captureFromCamera}
                    data-testid={`shopflow-capture-${step}`}
                    className="col-span-2 px-3 py-3 rounded-lg bg-gradient-to-r from-emerald-500 to-teal-500 text-white text-sm font-black flex items-center justify-center gap-1.5 hover:brightness-110 shadow-lg"
                  >
                    <CameraIcon className="w-4 h-4" /> Click Photo
                  </button>
                  <button onClick={cam.stop} className="col-span-2 text-[11px] text-slate-400 hover:text-white text-center py-1">Cancel camera</button>
                </>
              )}

              {(step === 1 && customerImg) && (
                <>
                  <button onClick={retakeCustomer} className="px-3 py-2 rounded-lg bg-white/10 hover:bg-white/20 text-white text-xs font-bold flex items-center justify-center gap-1"><RefreshCw className="w-3 h-3" /> Retake</button>
                  <button
                    onClick={() => setStep(2)}
                    data-testid="shopflow-next-2"
                    className="px-3 py-2 rounded-lg bg-gradient-to-r from-emerald-500 to-teal-500 text-white text-xs font-black flex items-center justify-center gap-1"
                  >
                    Next: Apparel <ChevronRight className="w-3 h-3" />
                  </button>
                </>
              )}
              {(step === 2 && apparelImg) && (
                <>
                  <button onClick={retakeApparel} className="px-3 py-2 rounded-lg bg-white/10 hover:bg-white/20 text-white text-xs font-bold flex items-center justify-center gap-1"><RefreshCw className="w-3 h-3" /> Retake</button>
                  <button
                    onClick={doMerge}
                    disabled={processing}
                    data-testid="shopflow-merge"
                    className="px-3 py-2 rounded-lg bg-gradient-to-r from-fuchsia-500 via-purple-500 to-amber-500 text-white text-xs font-black flex items-center justify-center gap-1 disabled:opacity-50 shadow-lg"
                  >
                    {processing ? <><Loader2 className="w-3 h-3 animate-spin" /> AI Processing…</> : <>🤖 AI Try-On (Real)</>}
                  </button>
                </>
              )}
            </div>

            {step === 2 && (
              <button onClick={() => setStep(1)} className="mt-2 text-[11px] text-slate-400 hover:text-white flex items-center gap-1 mx-auto"><ChevronLeft className="w-3 h-3" /> Back to Customer</button>
            )}

            {processing && status && (
              <div className="mt-3 text-center text-xs text-fuchsia-300">{status}</div>
            )}
          </div>
        </div>
      )}

      {/* STEP 3 — merged preview */}
      {modelReady && step === 3 && (
        <div className="flex-1 flex flex-col overflow-auto">
          <div className="flex-1 flex items-center justify-center bg-slate-950 p-3 relative">
            <canvas ref={canvasRef} className="max-w-full max-h-full shadow-2xl rounded-xl border border-white/10" data-testid="shopflow-canvas" />
            {/* 🆕 Stable snapshot overlay — persists even if canvas re-renders (fixes blank-after-few-seconds) */}
            {finalBlobUrl && (
              <img
                src={finalBlobUrl}
                alt="Final try-on"
                className="absolute inset-3 max-w-full max-h-full m-auto shadow-2xl rounded-xl border border-white/10 pointer-events-none"
                style={{ objectFit: 'contain' }}
                data-testid="shopflow-final-img"
              />
            )}
          </div>

          {/* 🔄 Compare toggle MOVED to controls bar — no more accidental tap on result */}
          <div className="bg-slate-950 border-t border-white/10 px-3 py-2 flex items-center justify-center gap-2 flex-wrap">
            <button
              onClick={() => setCompareMode((v) => !v)}
              data-testid="shopflow-compare-toggle"
              className={`px-3 py-1.5 rounded-lg text-[11px] font-black flex items-center gap-1.5 border transition ${compareMode ? 'bg-amber-500 border-amber-300 text-black' : 'bg-white/10 border-white/20 text-white hover:bg-white/20'}`}
              title="Compare Original ↔ Try-On"
            >
              {compareMode ? <><EyeOff className="w-3.5 h-3.5" /> Showing Original</> : <><Eye className="w-3.5 h-3.5" /> Compare with Original</>}
            </button>
          </div>

          {/* ⭐ Feedback — 👍/👎 (👎 auto-retries with stronger prompt once) */}
          {usedAiVton && (
            <div className="bg-slate-950 border-t border-white/10 px-3 py-2.5 flex items-center justify-center gap-2 flex-wrap" data-testid="shopflow-feedback-bar">
              <span className="text-[11px] text-slate-300 font-semibold">Kaisa laga?</span>
              <button
                onClick={() => submitFeedback('up')}
                disabled={!!feedbackRating || processing}
                data-testid="shopflow-feedback-up"
                className={`px-3 py-1.5 rounded-lg text-[11px] font-black flex items-center gap-1.5 border transition disabled:opacity-50 ${feedbackRating === 'up' ? 'bg-emerald-500 border-emerald-300 text-white' : 'bg-emerald-500/10 border-emerald-500/40 text-emerald-300 hover:bg-emerald-500/20'}`}
                title="Perfect result"
              >
                👍 <span>Bahut badhiya</span>
              </button>
              <button
                onClick={() => submitFeedback('down')}
                disabled={!!feedbackRating || processing}
                data-testid="shopflow-feedback-down"
                className={`px-3 py-1.5 rounded-lg text-[11px] font-black flex items-center gap-1.5 border transition disabled:opacity-50 ${feedbackRating === 'down' ? 'bg-rose-500 border-rose-300 text-white' : 'bg-rose-500/10 border-rose-500/40 text-rose-300 hover:bg-rose-500/20'}`}
                title="Bad result — retry with stronger prompt"
              >
                👎 <span>{retryAttempted ? 'Phir bhi kharab' : 'Sahi nahi'}</span>
              </button>
              {retryAttempted && <span className="text-[10px] text-amber-300 font-semibold">🔄 Retry used</span>}
            </div>
          )}

          {/* Controls bar */}
          <div className="bg-slate-950 border-t border-white/10 p-3 space-y-2">
            {/* Character selector */}
            <div className="flex items-center justify-center gap-1.5 flex-wrap">
              <div className="text-[10px] uppercase tracking-widest text-slate-400 font-black mr-1">Character:</div>
              {CHARACTERS.map((c) => (
                <button
                  key={c.id}
                  onClick={() => setCharacter(c.id)}
                  data-testid={`shopflow-char-${c.id}`}
                  className={`px-2.5 py-1 rounded-full text-[11px] font-black flex items-center gap-1 border transition ${character === c.id ? 'bg-fuchsia-500 border-fuchsia-400 text-white' : 'bg-white/5 border-white/10 text-slate-300 hover:bg-white/10'}`}
                >
                  <span>{c.emoji}</span> {c.label}
                </button>
              ))}
            </div>

            {/* Sliders (only for local overlay mode — AI VTON doesn't need positioning) */}
            {!usedAiVton && (
              <div className="grid grid-cols-3 gap-2 text-[10px] text-slate-300">
                <label className="flex flex-col gap-0.5">
                  <span>Size <span className="font-mono text-fuchsia-300">{scale.toFixed(2)}x</span></span>
                  <input type="range" min="0.6" max="1.6" step="0.05" value={scale} onChange={(e) => setScale(Number(e.target.value))} data-testid="shopflow-scale" />
                </label>
                <label className="flex flex-col gap-0.5">
                  <span>Move Y <span className="font-mono text-fuchsia-300">{offsetY}px</span></span>
                  <input type="range" min="-150" max="150" step="5" value={offsetY} onChange={(e) => setOffsetY(Number(e.target.value))} data-testid="shopflow-offy" />
                </label>
                <label className="flex flex-col gap-0.5">
                  <span>Move X <span className="font-mono text-fuchsia-300">{offsetX}px</span></span>
                  <input type="range" min="-150" max="150" step="5" value={offsetX} onChange={(e) => setOffsetX(Number(e.target.value))} data-testid="shopflow-offx" />
                </label>
              </div>
            )}
            {usedAiVton && (
              <>
                <div className="rounded-lg border border-fuchsia-400/40 bg-fuchsia-500/10 px-2 py-1.5 text-[10px] text-fuchsia-200 text-center font-black">
                  🤖 REAL AI TRY-ON · Powered by Google Nano Banana (Gemini image edit)
                </div>
                {/* Quota chip */}
                <QuotaBadge />
                {/* Prompt tuners — regenerate with different lighting/angle */}
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <div className="text-[9px] uppercase tracking-widest text-slate-400 font-black mb-1">Lighting</div>
                    <div className="flex gap-1 flex-wrap">
                      {['natural', 'studio', 'outdoor', 'indoor', 'golden-hour'].map((l) => (
                        <button key={l} onClick={() => setLighting(l)} data-testid={`shopflow-light-${l}`} className={`text-[10px] px-2 py-0.5 rounded-full font-bold ${lighting === l ? 'bg-amber-500 text-black' : 'bg-white/5 text-slate-300 border border-white/10'}`}>{l}</button>
                      ))}
                    </div>
                  </div>
                  <div>
                    <div className="text-[9px] uppercase tracking-widest text-slate-400 font-black mb-1">Angle</div>
                    <div className="flex gap-1 flex-wrap">
                      {['front', 'three-quarter', 'side', 'back'].map((a) => (
                        <button key={a} onClick={() => setAngle(a)} data-testid={`shopflow-angle-${a}`} className={`text-[10px] px-2 py-0.5 rounded-full font-bold ${angle === a ? 'bg-cyan-500 text-black' : 'bg-white/5 text-slate-300 border border-white/10'}`}>{a}</button>
                      ))}
                    </div>
                  </div>
                </div>
                <button onClick={doMerge} disabled={processing} data-testid="shopflow-regenerate" className="w-full px-3 py-2 rounded-lg bg-gradient-to-r from-fuchsia-500 to-purple-600 text-white text-xs font-black flex items-center justify-center gap-1 disabled:opacity-50">
                  {processing ? <><Loader2 className="w-3 h-3 animate-spin" /> Regenerating…</> : <>🔄 Regenerate with new settings</>}
                </button>
              </>
            )}

            {/* 🎁 Referral Booster — viral share incentive */}
            {user && (
              <div className="rounded-xl border-2 border-amber-400/40 bg-gradient-to-r from-amber-950/60 via-orange-950/50 to-amber-950/60 p-2.5" data-testid="shopflow-referral-booster">
                <div className="flex items-center justify-between gap-2 flex-wrap">
                  <div className="flex items-center gap-2 min-w-0">
                    <Gift className="w-5 h-5 text-amber-300 flex-shrink-0" />
                    <div className="min-w-0">
                      <div className="text-[10px] uppercase tracking-widest text-amber-300 font-black">🎁 Referral Booster</div>
                      <div className="text-[11px] text-slate-200 leading-tight">Share pe har naye sign-up par: <b className="text-amber-200">₹50 AT Wallet Bonus</b> to both!</div>
                    </div>
                  </div>
                  <label className="flex items-center gap-1 text-[11px] text-slate-300 cursor-pointer select-none" title="Include your referral link in WhatsApp share">
                    <input
                      type="checkbox"
                      checked={includeReferral}
                      onChange={(e) => setIncludeReferral(e.target.checked)}
                      data-testid="shopflow-referral-toggle"
                      className="accent-amber-400"
                    />
                    Include my referral link
                  </label>
                </div>
                {includeReferral && referralCode && (
                  <div className="mt-1.5 text-[10px] text-emerald-300 font-mono truncate" title={referralLink}>
                    🔗 {referralLink}
                  </div>
                )}
              </div>
            )}

            {/* Action buttons */}
            <div className="flex flex-wrap gap-2 justify-center pt-1">
              <button onClick={download} data-testid="shopflow-download" className="px-3 py-2 rounded-lg bg-gradient-to-r from-emerald-500 to-teal-500 text-white text-xs font-black flex items-center gap-1 hover:brightness-110"><Download className="w-3 h-3" /> Download</button>
              <button onClick={shareWhatsApp} data-testid="shopflow-share" className="px-3 py-2 rounded-lg bg-gradient-to-r from-green-500 to-emerald-600 text-white text-xs font-black flex items-center gap-1 hover:brightness-110"><Share2 className="w-3 h-3" /> Share to WhatsApp</button>
              <button onClick={() => setStep(2)} data-testid="shopflow-change-apparel" className="px-3 py-2 rounded-lg bg-white/10 hover:bg-white/20 text-white text-xs font-bold flex items-center gap-1"><RefreshCw className="w-3 h-3" /> Change Apparel</button>
              <button onClick={retakeCustomer} data-testid="shopflow-retake-customer" className="px-3 py-2 rounded-lg bg-white/10 hover:bg-white/20 text-white text-xs font-bold flex items-center gap-1"><ChevronLeft className="w-3 h-3" /> Retake Customer</button>
              <button onClick={resetAll} data-testid="shopflow-reset" className="px-3 py-2 rounded-lg bg-white/10 hover:bg-white/20 text-white text-xs font-bold flex items-center gap-1"><RotateCcw className="w-3 h-3" /> New Session</button>
            </div>

            <div className="text-[9px] text-slate-500 text-center pt-1">
              💡 MediaPipe Pose + Clothes Segmentation · 100% Browser · Zero cloud upload
            </div>
          </div>
        </div>
      )}

      {/* 📦 CATALOG PICKER MODAL */}
      {showCatalog && (
        <div className="fixed inset-0 z-[60] bg-black/85 flex items-center justify-center p-4" onClick={() => setShowCatalog(false)} data-testid="shopflow-catalog-modal">
          <div className="max-w-3xl w-full max-h-[80vh] rounded-2xl border-2 border-amber-400/60 bg-slate-950 p-4 overflow-hidden flex flex-col" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-3">
              <div>
                <div className="text-[10px] uppercase tracking-widest text-amber-300 font-black">Pre-Uploaded Catalog</div>
                <h3 className="text-lg font-black text-white">📦 Choose Apparel ({catalogProducts.length})</h3>
              </div>
              <button onClick={() => setShowCatalog(false)} className="p-2 rounded-lg bg-white/10 hover:bg-white/20"><X className="w-4 h-4" /></button>
            </div>
            {catalogProducts.length === 0 ? (
              <div className="text-xs text-slate-500 text-center py-8">
                Koi pre-uploaded apparel nahi. Admin panel se products add karein.
              </div>
            ) : (
              <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 gap-2 overflow-y-auto pr-1">
                {catalogProducts.map((p) => (
                  <button
                    key={p.id}
                    onClick={() => pickFromCatalog(p)}
                    data-testid={`shopflow-catalog-item-${p.id}`}
                    className="rounded-xl overflow-hidden border-2 border-white/10 hover:border-amber-400 bg-slate-900 transition group"
                  >
                    <div className="aspect-square bg-black/40">
                      <img src={p.image_url} alt={p.name} loading="lazy" className="w-full h-full object-cover group-hover:scale-105 transition"
                        onError={(e) => { e.currentTarget.style.opacity = '0.3'; }} />
                    </div>
                    <div className="text-[10px] text-white font-bold truncate p-1.5">{p.name}</div>
                    {p.subcategory && <div className="text-[9px] text-slate-400 truncate px-1.5 pb-1.5">{p.subcategory}</div>}
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default TrialRoomShopFlow;
